import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { whenOnce } from "@arcgis/core/core/watchUtils";
import WFSLayer from '@arcgis/core/layers/WFSLayer';
import { MapContext } from '../../../../App/MapContextContainer';
import notificationsRenderer from '../../../../components/Maps/NotificationsRenderer';
import { useSetUpMap } from '../../../../App/useSetUpMap';
import MapContainer from '../../../../components/Maps/MapContainer';
import * as promiseUtils from "@arcgis/core/core/promiseUtils";
import * as projection from "@arcgis/core/geometry/projection";
import { PREFERRED_WKID } from '../../../CustomAreas/UserDataContextContainer';
import Extent from '@arcgis/core/geometry/Extent';
import { DECLARATIONS_MIN_SCALE_TO_SHOW, newDeclarationsApiUrl, oldDeclarationsApiUrl } from '../../../../App/useNotificationsLayer';
import PopupTemplate from '@arcgis/core/PopupTemplate';
import useSetPopupContent from '../../../../App/useSetPopupContent';
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";
import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import Graphic from '@arcgis/core/Graphic';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Polygon from '@arcgis/core/geometry/Polygon';
import { ForestsNearUsContext } from '../../ForestsNearUsContextContainer';
import StatisticDefinition from '@arcgis/core/rest/support/StatisticDefinition';
import { useAdditionalDebugging } from '../../utils/debuggingUtilities';
import useBboxUpdate from './useBboxUpdate';
import intersectingForestMaskLayerPopup from './intersectingForestMaskLayerPopup';
import { geodesicArea, intersect } from '@arcgis/core/geometry/geometryEngine';
import useTestLayer from '../../../../App/useTestLayer';
import errors from '../../../../App/errors';
import { useTranslation } from 'react-i18next';
import useOpenAreasLayer from '../../../../components/Maps/useOpenAreasLayer';

const urbanAreasLayerUrl = "https://avoin-paikkatieto.maanmittauslaitos.fi/maastotiedot/features/v1/collections/taajaanrakennettualue/items";
const forestMaskLayerUrl = "https://avoin.metsakeskus.fi/rajapinnat/v1/forestmask/ows";

export default function ForestsNearUsMap() {
    const mapContext = useContext(MapContext);
    const elementRef = useRef(null);
    const { webmap, view } = useSetUpMap(elementRef, "6e34c9c7b08d41779b23bf1241649275");
    const viewWatcher = useRef<__esri.WatchHandle | null>(null);
    const setPopupContent = useSetPopupContent();
    const forestsNearUsContext = useContext(ForestsNearUsContext);
    const [selectedAreaBBox, setSelectedAreaBBox] = useState<Extent | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const { t, i18n } = useTranslation("nearForests");

    const preferredSpatialReference = {
        wkid: PREFERRED_WKID
    };

    const declarationsLayerUrl = process.env.REACT_APP_USE_OLD_DECLARATION_URL === "true" ? oldDeclarationsApiUrl : newDeclarationsApiUrl;

    const notificationsLayer = useMemo(() => new WFSLayer({
        url: declarationsLayerUrl,
        title: t("mkiKerrosOtsikko"),
        copyright: "Suomen metsäkeskus",
        visible: false,
        minScale: DECLARATIONS_MIN_SCALE_TO_SHOW,
        spatialReference: { wkid: 4326 },
        renderer: notificationsRenderer,
        customParameters: {
            cql_filter: "1 = 0"
        }
    }), []);

    useTestLayer(declarationsLayerUrl + "?SERVICE=WFS&REQUEST=GetCapabilities&VERSION=2.0.0&format=json", errors.notificationLayerLoadError);

    const openAreasLayer = useOpenAreasLayer(view);

    const urbanAreasPopupTemplate = useMemo(() => new PopupTemplate({
        title: t("taajamaPopupOtsikko"),
        outFields: ["mtk_id"],
        content: [{ type: "fields" }],
        fieldInfos: [{
            fieldName: "mtk_id",
        }]
    }), [t, i18n.language]);

    const urbanAreasLayer = useMemo(() => new GeoJSONLayer({
        url: urbanAreasLayerUrl,
        popupEnabled: true,
        title: t("taajamaKerrosOtsikko"),
        popupTemplate: urbanAreasPopupTemplate,
        visible: true,
        geometryType: "polygon",
        minScale: DECLARATIONS_MIN_SCALE_TO_SHOW,
        customParameters: {
            "api-key": `${process.env.REACT_APP_MML_API_KEY}`,
            "mtk_id": "99999999999999"
        },
        fields: [{
            name: "mtk_id",
            type: "double"
        }],
    }), []);

    const urbanAreasLayerBboxUpdate = useBboxUpdate(view, urbanAreasLayer, {
        "api-key": `${process.env.REACT_APP_MML_API_KEY}`
    });

    useTestLayer(urbanAreasLayerUrl + `?api-key=${process.env.REACT_APP_MML_API_KEY}`, { ...errors.layerLoadingFailed, replacementValues: ["taajamat"] });

    const maskLayerPopup = useMemo(() => new PopupTemplate({
        title: t("metsämaskiPopupOtsikko"),
        outFields: ["area"],
        content: [{ type: "fields" }],
        fieldInfos: [{
            fieldName: "area",
            label: "Pinta-ala (ha)",
            format: {
                places: 1,
                digitSeparator: true
            }
        }]
    }), [t, i18n.language]);

    const forestMaskRenderer = new SimpleRenderer({
        symbol: new SimpleFillSymbol({
            color: "lightgreen"
        })
    });

    const forestMaskLayer = useMemo(() => new WFSLayer({
        url: forestMaskLayerUrl,
        title: t("metsämaskiKerrosOtsikko"),
        popupEnabled: true,
        renderer: forestMaskRenderer,
        visible: false,
        opacity: 0.5,
        popupTemplate: maskLayerPopup,
        copyright: "Suomen metsäkeskus",
        minScale: DECLARATIONS_MIN_SCALE_TO_SHOW,
        spatialReference: { wkid: 4326 },
        customParameters: {
            cql_filter: "1 = 0"
        }
    }), []);

    const { isDebugOn, debugRectangles, drawBBoxOfAreaWeWantToSave, debugSquare } = useAdditionalDebugging(webmap, view, urbanAreasLayer, forestMaskLayer);

    const forestMaskLayerBboxUpdate = useBboxUpdate(view, forestMaskLayer, {
        propertyName: `(geom,area)`
    });

    useTestLayer(forestMaskLayerUrl + "?SERVICE=WFS&REQUEST=GetCapabilities&VERSION=2.0.0&format=json", { ...errors.layerLoadingFailed, replacementValues: ["metsämaski"] });

    const updateNotificationsLayerBBOX = promiseUtils.debounce(() => {
        return new Promise((resolve) => {
            let geometryInPreferredWkid = projection.project(view.extent, preferredSpatialReference) as Extent;

            const {
                xmin,
                ymin,
                xmax,
                ymax,
                spatialReference: { wkid }
            } = geometryInPreferredWkid;

            notificationsLayer.customParameters = {
                propertyName: `(FORESTUSEDECLARATIONNUMBER,DECLARATIONARRIVALDATE,AREA,CUTTINGREALIZATIONPRACTICE,GEOMETRY,MEANAGE)`,
                cql_filter:
                    `CUTTINGREALIZATIONPRACTICE = 5 AND ` +
                    `BBOX(GEOMETRY,${xmin},${ymin},${xmax},${ymax},'EPSG:4326')`
            };

            notificationsLayer.refresh();

            whenOnce(notificationsLayer, "refresh", resolve);
        });
    });

    const forestsNearUsRenderer = new SimpleRenderer({
        symbol: new SimpleFillSymbol({ color: [0, 198, 94, 255], outline: { width: 0 } })
    });

    const forestsNearUsLayer = useMemo(() => new FeatureLayer({
        source: [],
        renderer: forestsNearUsRenderer,
        spatialReference: { wkid: 4326 },
        popupEnabled: true,
        popupTemplate: intersectingForestMaskLayerPopup,
        fields: [{
            name: "__esri_wfs_id__",
            type: "oid"
        }, {
            name: "area",
            type: "double"
        },
        {
            name: "bufferedAmount",
            type: "double"
        },
        {
            name: "iteration",
            type: "double"
        }],
        title: t("lähimetsätKerrosOtsikko"),
        geometryType: "polygon"
    }), []);

    useEffect(() => {
        if (forestMaskLayer) {
            forestMaskLayer.title = t("metsämaskiKerrosOtsikko");
        }

        if (forestsNearUsLayer) {
            forestsNearUsLayer.title = t("lähimetsätKerrosOtsikko");
        }

        if (urbanAreasLayer) {
            urbanAreasLayer.title = t("taajamaKerrosOtsikko");
        }

        if (notificationsLayer) {
            notificationsLayer.title = t("mkiKerrosOtsikko");
        }
    }, [t, i18n.language]);

    useEffect(() => {
        const asyncCall = async () => {
            let geometryInPreferredWkid = selectedAreaBBox ? selectedAreaBBox : projection.project(view.extent, preferredSpatialReference) as Extent;

            await drawBBoxOfAreaWeWantToSave(geometryInPreferredWkid);

            if (forestsNearUsContext.forestsNearUsState.selectedAreaToSaveId) {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/api/SaveNearForestsForBBox`, {
                    body: JSON.stringify({
                        bbox: geometryInPreferredWkid,
                        areaId: forestsNearUsContext.forestsNearUsState.selectedAreaToSaveId,
                        communityId: forestsNearUsContext.forestsNearUsState.communityId
                    }),
                    method: "POST"
                });
            }
        }

        if (forestsNearUsContext.forestsNearUsState.selectedAreaToSaveId) {
            asyncCall();
        }
    }, [forestsNearUsContext.forestsNearUsState.selectedAreaToSaveId]);

    useEffect(() => {
        const asyncCall = async () => {
            try {
                forestsNearUsContext.forestsNearUsStateDispatch({ type: 'setAreaLoadError', isInError: false });

                const getAllFeaturesQuery = forestsNearUsLayer.createQuery();
                const allFeaturesSet = await forestsNearUsLayer.queryFeatures(getAllFeaturesQuery);

                await forestsNearUsLayer.applyEdits({
                    deleteFeatures: allFeaturesSet.features
                });

                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/api/GetNearForestsGeography/` +
                    `${forestsNearUsContext.forestsNearUsState.selectedAreaToGetId}`, {
                    method: "GET"
                });

                const fromServerJson = await res.json();

                const bufferedGraphics: Graphic[] = [];

                console.log("Got " + fromServerJson.features.length + " features from server");

                const extentForArea: Extent = Extent.fromJSON(fromServerJson.bbox);

                // const geometryInPreferredWkid = projection.project(view.extent, preferredSpatialReference) as Extent;
                const bbox = [extentForArea.extent.xmin, extentForArea.extent.ymin, extentForArea.extent.xmax, extentForArea.extent.ymax];

                fromServerJson.features.forEach((feature: any, index: number) => {
                    const polygon = new Polygon({
                        rings: feature.geometry.coordinates,
                        spatialReference: {
                            wkid: PREFERRED_WKID
                        }
                    });

                    const g = new Graphic({
                        attributes: feature.properties,
                        geometry: polygon
                    });
                    bufferedGraphics.push(g);
                });

                const addEdits = {
                    addFeatures: bufferedGraphics
                };

                if (isDebugOn) {
                    // debugRectangles([extentForArea]);

                    fromServerJson.grid && Array.isArray(fromServerJson.grid) && fromServerJson.grid.forEach((feature: any) => {
                        const g = new Graphic({
                            geometry: new Polygon({
                                rings: feature.geometry.coordinates,
                                spatialReference: {
                                    wkid: PREFERRED_WKID
                                }
                            })
                        });

                        debugRectangles([g.geometry]);
                    });
                }

                await forestsNearUsLayer.applyEdits(addEdits);

                mapContext.navigateToPoint(extentForArea);

                setSelectedAreaBBox(extentForArea);
            } catch (error) {
                forestsNearUsContext.forestsNearUsStateDispatch({ type: 'setAreaLoadError', isInError: true });
            }
            finally {
                forestsNearUsContext.forestsNearUsStateDispatch({ type: "setIsAreaLoading", isLoading: false });
            }
        }

        if (forestsNearUsContext.forestsNearUsState.selectedAreaToGetId) {
            asyncCall();
        }
    }, [forestsNearUsContext.forestsNearUsState.selectedAreaToGetId]);

    const updateStatisticsAsync = async () => {
        const queryNearForests = forestsNearUsLayer.createQuery();
        queryNearForests.geometry = view.extent;
        queryNearForests.returnGeometry = true;

        const featuresWithEnoughSize = await forestsNearUsLayer.queryFeatures(queryNearForests);

        let totalSizeOfForestsNearUs = 0;

        if (featuresWithEnoughSize.features.length > 0) {
            for (const feature of featuresWithEnoughSize.features) {
                const intersectionArea = intersect(view.extent, feature.geometry);
                const alwaysArray = Array.isArray(intersectionArea) ? intersectionArea : [intersectionArea];

                alwaysArray.forEach(element => {
                    const thisSize = geodesicArea(element as Polygon, "hectares");
                    totalSizeOfForestsNearUs += thisSize;
                });
            }
        }

        forestsNearUsContext.forestsNearUsStateDispatch({
            type: "setNearForestAreaSize",
            size: totalSizeOfForestsNearUs,
            count: featuresWithEnoughSize.features.length
        });

        setIsUpdating(false);
    }

    useEffect(() => {
        setIsUpdating(true);

        updateStatisticsAsync();
    }, [view.extent]);

    const setDefinitionExpression = () => {
        let desiredAreaSizeExpression: string = "1=1";
        let desiredAreaSizeMetersExpression: string = "bufferedAmount >= 0";

        if (forestsNearUsContext.forestsNearUsState.desiredAreaSize && forestsNearUsContext.forestsNearUsState.desiredAreaSize > 0) {
            desiredAreaSizeExpression = "area >= " + forestsNearUsContext.forestsNearUsState.desiredAreaSize;
        }

        if (forestsNearUsContext.forestsNearUsState.desiredAreaSizeMeters && forestsNearUsContext.forestsNearUsState.desiredAreaSizeMeters > 0) {
            desiredAreaSizeMetersExpression = "bufferedAmount >= " + forestsNearUsContext.forestsNearUsState.desiredAreaSizeMeters / 2; // Buffered amount is half in the data
        }

        forestsNearUsLayer.definitionExpression = desiredAreaSizeExpression + " and " + desiredAreaSizeMetersExpression;
    }

    useEffect(() => {
        setIsUpdating(true);

        setDefinitionExpression();

        updateStatisticsAsync();
    }, [forestsNearUsContext.forestsNearUsState.desiredAreaSize, forestsNearUsContext.forestsNearUsState.desiredAreaSizeMeters]);

    let sumArea = new StatisticDefinition({
        onStatisticField: "area",
        outStatisticFieldName: "sumArea",
        statisticType: "sum"
    });

    const handleForestMaskUpdate = async () => {
        // setIsUpdating(true);

        const queryArea = forestMaskLayer.createQuery();

        queryArea.outStatistics = [sumArea];

        const response = await forestMaskLayer.queryFeatures(queryArea);

        if (response.features.length > 0) {
            forestsNearUsContext.forestsNearUsStateDispatch({ type: 'setTotalForestAreaSize', size: response.features[0].attributes.sumArea });
        }

        // setIsUpdating(false);
    }

    useEffect(() => {
        if (mapContext.mapAndViewReady) {
            webmap.add(notificationsLayer);
            webmap.add(urbanAreasLayer);
            webmap.add(openAreasLayer);
            webmap.add(forestMaskLayer);
            webmap.add(forestsNearUsLayer);

            // const measurement = new Measurement({
            //     view: view,
            //     activeTool: "distance"
            // });

            // view.ui.add(measurement, "bottom-right");

            const notificationsLayerPopup = new PopupTemplate({
                title: "Lisätietoja",
                outFields: ["FORESTUSEDECLARATIONNUMBER", "DECLARATIONARRIVALDATE", "MEANAGE", "CUTTINGREALIZATIONPRACTICE", "AREA"],
                content: setPopupContent
            });

            notificationsLayer.popupTemplate = notificationsLayerPopup;

            if (viewWatcher.current !== null) {
                viewWatcher.current.remove();
            }

            viewWatcher.current = view.watch("stationary", (stationary) => {
                if (stationary && view.scale <= DECLARATIONS_MIN_SCALE_TO_SHOW) {
                    updateNotificationsLayerBBOX().catch();
                }
            });

            forestMaskLayer.on("refresh", async function (event) {
                if (event.dataChanged) {
                    await handleForestMaskUpdate();
                }
            });

            return () => {
                if (viewWatcher.current !== null) {
                    viewWatcher.current.remove();
                }
            }
        }
    }, [mapContext.mapAndViewReady, webmap, isDebugOn]);

    return <MapContainer customVisibilityCheckFn={() => isUpdating} elementRef={elementRef} view={view}></MapContainer>
}
